import React, { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import menuImportApi from '../../../services/menuImportApi';

const STATUS = {
  Pending: 0,
  Processing: 10,
  ReadyForReview: 20,
  Failed: 30,
  Imported: 40,
  Cancelled: 50,
};

const STATUS_LABELS = {
  0: 'Queued',
  10: 'Analyzing menu with AI...',
  20: 'Ready for review',
  30: 'Failed',
  40: 'Imported',
  50: 'Cancelled',
};

const ProcessingStep = ({ jobId, onReady, onFailed }) => {
  const [statusCode, setStatusCode] = useState(STATUS.Pending);
  const [errorMessage, setErrorMessage] = useState('');
  const intervalRef = useRef(null);
  const delayRef = useRef(3000);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await menuImportApi.status(jobId);
        const data = res?.data?.data;
        if (!data || cancelled) return;

        setStatusCode(data.status);
        setErrorMessage(data.errormessage || '');

        if (data.status === STATUS.ReadyForReview) {
          onReady(data);
          return true;
        }
        if (data.status === STATUS.Failed) {
          onFailed(data.errormessage || 'Processing failed');
          return true;
        }
        if (data.status === STATUS.Imported && data.parsedmenu) {
          onReady(data);
          return true;
        }
      } catch (err) {
        if (!cancelled) {
          onFailed(err?.response?.data || err?.message || 'Status check failed');
        }
        return true;
      }
      return false;
    };

    const schedule = () => {
      intervalRef.current = setTimeout(async () => {
        const done = await poll();
        if (!done && !cancelled) {
          delayRef.current = Math.min(delayRef.current + 1000, 10000);
          schedule();
        }
      }, delayRef.current);
    };

    poll().then((done) => {
      if (!done) schedule();
    });

    return () => {
      cancelled = true;
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [jobId, onReady, onFailed]);

  return (
    <div className="mx-auto max-w-lg rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <Spin size="large" />
      <h2 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
        Processing your menu
      </h2>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        {STATUS_LABELS[statusCode] || 'Working...'}
      </p>
      <p className="mt-4 text-xs text-slate-400">
        This may take up to a minute. OCR, understanding, and schema mapping run in the background.
      </p>
      {errorMessage && statusCode === STATUS.Failed && (
        <p className="mt-4 text-sm text-rose-600">{errorMessage}</p>
      )}
    </div>
  );
};

export default ProcessingStep;
